import { useState, useEffect, useRef, FormEvent, ChangeEvent } from 'react';
import { supabase } from '../lib/supabase';
import { PhoneBookEntry, PhoneBookRequest, ShivarOption } from '../types';
import { validateContact } from '../utils/validation';
import { normalizePhone } from '../utils/phone';
import { formatShivar } from '../utils/shivar';
import { syncToGoogleSheets } from '../utils/googleSheets';
import { useAuth } from '../contexts/AuthContext';
import { ErrorAlert } from './ui/ErrorAlert';
import {
  createHybridTransliterationEngine,
  marathiPhoneticExpanded,
  type TransliterationEngine,
} from 'lipic-js';
import { IndicTransliterate } from '@ai4bharat/indic-transliterate';

interface ContactFormProps {
  initial?: Partial<PhoneBookEntry>;
  isRequest?: boolean;
  minimal?: boolean;
  onSuccess?: () => void;
  onCancel?: () => void;
}

export const ContactForm = ({ initial, isRequest, minimal, onSuccess, onCancel }: ContactFormProps) => {
  const { user } = useAuth();
  const [values, setValues] = useState({
    marathi_name: initial?.marathi_name ?? '',
    name: initial?.name ?? '',
    phone_number: initial?.phone_number?.startsWith('+91') ? initial.phone_number.slice(3) : initial?.phone_number ?? '',
    alternate_phone_number: initial?.alternate_phone_number?.startsWith('+91') ? initial.alternate_phone_number.slice(3) : initial?.alternate_phone_number ?? '',
    shivar: initial?.shivar ?? '',
    village: initial?.village ?? '',
    taluka: initial?.taluka ?? '',
    district: initial?.district ?? '',
    address: initial?.address ?? '',
    category: initial?.category ?? '',
    notes: '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [duplicateInfo, setDuplicateInfo] = useState<{ source: 'phone_book' | 'request'; name?: string } | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [shivars, setShivars] = useState<ShivarOption[]>([]);
  const [listening, setListening] = useState<string | null>(null);
  const [speechError, setSpeechError] = useState<string | null>(null);
  const marathiEditedRef = useRef(false);
  const engineRef = useRef<TransliterationEngine | null>(null);
  const [engineReady, setEngineReady] = useState(false);

  useEffect(() => {
    const loadShivars = async () => {
      const { data, error: dbError } = await supabase.from('shivars').select('name, marathi_name').order('name');
      if (!dbError) {
        setShivars(
          (data ?? []).map((s) => ({
            value: s.name,
            label: formatShivar(s.name, s.marathi_name),
          }))
        );
      }
    };
    loadShivars();
  }, []);

  useEffect(() => {
    let mounted = true;
    const initEngine = async () => {
      try {
        const engine = await createHybridTransliterationEngine(
          {
            expandedMap: marathiPhoneticExpanded,
            scriptId: 'devanagari',
            languageId: 'mr',
          },
          { isWasm: false }
        );
        if (mounted) {
          engineRef.current = engine;
          setEngineReady(true);
        }
      } catch (err) {
        console.error('lipic-js engine init failed', err);
      }
    };
    initEngine();
    return () => { mounted = false; };
  }, []);

  useEffect(() => {
    if (!engineReady || !engineRef.current) return;
    const name = values.name.trim();
    if (!name) {
      setValues((prev) => ({ ...prev, marathi_name: '' }));
      marathiEditedRef.current = false;
      return;
    }
    if (marathiEditedRef.current) return;
    engineRef.current.reset();
    const edit = engineRef.current.processText(name);
    setValues((prev) => ({ ...prev, marathi_name: edit.insert }));
  }, [values.name, engineReady]);

  const handleChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setValues((prev) => ({ ...prev, [name]: value }));
  };

  const handleMarathiChange = (text: string) => {
    marathiEditedRef.current = text.trim().length > 0;
    setValues((prev) => ({ ...prev, marathi_name: text }));
  };

  const startSpeech = (field: 'name' | 'marathi_name') => {
    const SpeechRecognitionAPI = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    if (!SpeechRecognitionAPI) {
      setSpeechError('Speech recognition is not supported in this browser.');
      return;
    }
    const recognition = new SpeechRecognitionAPI();
    recognition.lang = field === 'name' ? 'en-US' : 'mr-IN';
    recognition.continuous = false;
    recognition.interimResults = false;
    setSpeechError(null);
    setListening(field);
    recognition.onresult = (event: any) => {
      const transcript = event.results[0][0].transcript;
      if (field === 'name') {
        marathiEditedRef.current = false;
        setValues((prev) => ({ ...prev, name: transcript }));
      } else {
        handleMarathiChange(transcript);
      }
    };
    recognition.onerror = (event: any) => {
      setSpeechError(`Speech error: ${event.error}`);
      setListening(null);
    };
    recognition.onend = () => setListening(null);
    recognition.start();
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setDuplicateInfo(null);
    setFieldErrors({});

    const isOther = values.shivar === '__other__';
    const effectiveShivar = isOther ? 'Other' : values.shivar.trim();
    const validation = validateContact({
      marathi_name: values.marathi_name,
      name: values.name,
      phone_number: values.phone_number,
      shivar: effectiveShivar,
      alternate_phone_number: values.alternate_phone_number || null,
    });
    if (validation.length > 0) {
      const errors: Record<string, string> = {};
      validation.forEach((err) => (errors[err.field] = err.message));
      setFieldErrors(errors);
      return;
    }

    setSaving(true);
    try {
      const normalized = normalizePhone(values.phone_number);
      const alternate = values.alternate_phone_number ? normalizePhone(values.alternate_phone_number) : null;

      if (isRequest) {
        const { data: dup } = await (supabase as any).rpc('check_contact_duplicate', { p_phone_number: normalized });
        if (dup) {
          const duplicate = dup as { source: string; name?: string | null };
          if (duplicate.source === 'phone_book') {
            setDuplicateInfo({ source: 'phone_book', name: duplicate.name ?? undefined });
          } else {
            setDuplicateInfo({ source: 'request' });
          }
          setSaving(false);
          return;
        }
      } else {
        const phoneBookQuery = supabase
          .from('phone_book')
          .select('id, name')
          .eq('phone_number', normalized);
        const { data: existingPhoneBook } = initial?.id
          ? await phoneBookQuery.neq('id', initial.id).limit(1)
          : await phoneBookQuery.limit(1);
        if (existingPhoneBook && existingPhoneBook.length > 0) {
          const existingName = String(existingPhoneBook[0].name || '');
          setDuplicateInfo({ source: 'phone_book', name: existingName });
          setSaving(false);
          return;
        }
      }

      const payload = {
        marathi_name: values.marathi_name,
        name: values.name,
        phone_number: normalized,
        alternate_phone_number: alternate,
        shivar: effectiveShivar || null,
        village: isOther ? values.village || null : null,
        taluka: isOther ? values.taluka || null : null,
        district: isOther ? values.district || null : null,
        address: isOther ? values.address || null : null,
        category: isOther ? values.category || null : null,
      };

      if (isRequest) {
        const { error: dbError } = await supabase.from('phone_book_requests').insert({
          ...payload,
          notes: isOther ? values.notes || null : null,
          requested_by: user?.id,
        } as PhoneBookRequest);
        if (dbError) throw dbError;
      } else if (initial?.id) {
        const { error: dbError } = await supabase.from('phone_book').update({
          ...payload,
          updated_at: new Date().toISOString(),
        }).eq('id', initial.id);
        if (dbError) throw dbError;
      } else {
        const { data: inserted, error: dbError } = await supabase.from('phone_book').insert({
          ...payload,
          created_by: user?.id,
          status: 'APPROVED',
        } as PhoneBookEntry).select();
        if (dbError) throw dbError;
        if (inserted && inserted.length > 0) {
          await syncToGoogleSheets(inserted[0] as Record<string, unknown>);
        }
      }
      onSuccess?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const inputClass = (name: string) =>
    `w-full rounded border px-3 py-2 text-sm ${fieldErrors[name] ? 'border-red-500' : 'border-gray-300'}`;

  const isOther = values.shivar === '__other__';
  const effectiveShivar = isOther ? 'Other' : values.shivar.trim();
  const liveValidation = validateContact({
    marathi_name: values.marathi_name,
    name: values.name,
    phone_number: values.phone_number,
    shivar: effectiveShivar,
    alternate_phone_number: values.alternate_phone_number || null,
  });
  const isFormValid = liveValidation.length === 0;
  const requiredFilled =
    values.name.trim().length > 0 &&
    values.marathi_name.trim().length > 0 &&
    values.phone_number.trim().length > 0 &&
    values.shivar.trim().length > 0;
  const canSubmit = minimal ? requiredFilled : isFormValid;

  return (
    <form onSubmit={handleSubmit} className="space-y-4 rounded-lg border border-gray-200 bg-white p-4">
      {duplicateInfo && (
        <div className="rounded-md bg-green-50 p-4 text-green-800">
          {duplicateInfo.source === 'phone_book' ? (
            <p>
              This number is already added in the phone book with name:{' '}
              <span className="font-bold text-green-600">{duplicateInfo.name}</span>
            </p>
          ) : (
            <p>Your contact request is already under review. Please contact admin.</p>
          )}
        </div>
      )}
      {error && <ErrorAlert message={error} />}
      {speechError && <p className="text-sm text-red-600">{speechError}</p>}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700">Name *</label>
          <div className="flex gap-2">
            <input name="name" value={values.name} onChange={handleChange} className={`${inputClass('name')} flex-1`} />
            <button
              type="button"
              onClick={() => startSpeech('name')}
              disabled={listening === 'name'}
              className="rounded bg-gray-100 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-200 disabled:opacity-50"
            >
              {listening === 'name' ? 'Listening...' : 'Mic'}
            </button>
          </div>
          {fieldErrors.name && <p className="mt-1 text-xs text-red-600">{fieldErrors.name}</p>}
        </div>
        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700">Marathi Name *</label>
          <div className="flex gap-2">
            <IndicTransliterate
              value={values.marathi_name}
              onChangeText={handleMarathiChange}
              lang="mr"
              renderComponent={(props: any) => <input {...props} className={`${inputClass('marathi_name')} flex-1`} lang="mr" />}
            />
            <button
              type="button"
              onClick={() => startSpeech('marathi_name')}
              disabled={listening === 'marathi_name'}
              className="rounded bg-gray-100 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-200 disabled:opacity-50"
            >
              {listening === 'marathi_name' ? 'Listening...' : 'Mic'}
            </button>
          </div>
          {fieldErrors.marathi_name && <p className="mt-1 text-xs text-red-600">{fieldErrors.marathi_name}</p>}
        </div>
        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700">Phone Number *</label>
          <input name="phone_number" value={values.phone_number} onChange={handleChange} className={inputClass('phone_number')} />
          {fieldErrors.phone_number && <p className="mt-1 text-xs text-red-600">{fieldErrors.phone_number}</p>}
        </div>
        {!minimal && (
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Alternate Phone</label>
            <input
              name="alternate_phone_number"
              value={values.alternate_phone_number}
              onChange={handleChange}
              className={inputClass('alternate_phone_number')}
            />
            {fieldErrors.alternate_phone_number && <p className="mt-1 text-xs text-red-600">{fieldErrors.alternate_phone_number}</p>}
          </div>
        )}
        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700">Shivar *</label>
          <select name="shivar" value={values.shivar} onChange={handleChange} className={inputClass('shivar')} required>
            <option value="">{shivars.length === 0 ? 'No shivars added' : 'Select shivar'}</option>
            {shivars.map((shivar) => (
              <option key={shivar.value} value={shivar.value}>{shivar.label}</option>
            ))}
            <option value="__other__">Other</option>
          </select>
          {fieldErrors.shivar && <p className="mt-1 text-xs text-red-600">{fieldErrors.shivar}</p>}
        </div>
        {isOther && (
          <>
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">Village</label>
              <input name="village" value={values.village} onChange={handleChange} className={inputClass('village')} />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">Taluka</label>
              <input name="taluka" value={values.taluka} onChange={handleChange} className={inputClass('taluka')} />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">District</label>
              <input name="district" value={values.district} onChange={handleChange} className={inputClass('district')} />
            </div>
            <div className="md:col-span-2">
              <label className="mb-1 block text-sm font-medium text-gray-700">Address</label>
              <textarea name="address" value={values.address} onChange={handleChange} className={inputClass('address')} rows={3} />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">Category</label>
              <input name="category" value={values.category} onChange={handleChange} className={inputClass('category')} />
            </div>
          </>
        )}
        {isRequest && isOther && (
          <div className="md:col-span-2">
            <label className="mb-1 block text-sm font-medium text-gray-700">Notes</label>
            <textarea name="notes" value={values.notes} onChange={handleChange} className={inputClass('notes')} rows={2} />
          </div>
        )}
      </div>
      <div className="flex justify-end gap-2">
        {onCancel && (
          <button type="button" onClick={onCancel} className="rounded border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50">
            Cancel
          </button>
        )}
        <button
          type="submit"
          disabled={saving || !canSubmit}
          className="rounded bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
        >
          {saving ? 'Saving...' : initial?.id ? 'Update Contact' : minimal ? 'Add Contact' : isRequest ? 'Submit Request' : 'Save'}
        </button>
      </div>
    </form>
  );
};
